import type { NatsConnection, Subscription } from "@nats-io/nats-core";
import { addBreadcrumb, captureException } from "@web-monorepo/telemetry";
import {
  RealtimeAdapter,
  type RealtimeMetrics,
  type RealtimeSubscription,
  getClientNetworkState,
  getPermissionViolationFingerprint,
} from "./types";

/**
 * NATS Connection Lifecycle
 * ========================
 *
 * Initial Connection:
 *   init() -> wsconnect() -> connection established -> onConnect fires -> monitorConnection()
 *
 * Connection Loss & Reconnection:
 *   1. Connection drops (network issue, server restart, etc.)
 *   2. NATS library automatically attempts reconnection
 *   3. reconnectDelayHandler() called before each attempt:
 *      - Returns "full jitter" backoff: random in [0, min(2^attempt * 1s, 30s)]
 *      - Cap sequence: ~2s -> ~4s -> ~8s -> ~16s -> 30s (max)
 *      - Spreads simultaneous client reconnects across the full window to avoid
 *        thundering-herd pressure on the auth-callout responder during pod rotations
 *   4. On successful reconnect:
 *      - Status event "reconnect" fires -> onReconnect callback
 *      - #reconnectAttempts resets to 0, refreshing both the backoff and the
 *        exhaustion budget, because a recovered episode is not a failure
 *   5. On the MAX_RECONNECT_ATTEMPTS-th (20th) consecutive failed dial with no
 *      intervening successful reconnect:
 *      - onConnectionExhausted callback fires (if provided)
 *      - Connection closes permanently
 *
 * Dead Connection Detection:
 *   - Ping sent every PING_INTERVAL_MS (10s)
 *   - After MAX_PING_OUT (2) missed pongs, connection considered dead
 *   - Total detection time: ~20s worst case
 *
 * Graceful Shutdown:
 *   close() -> nullify callback -> close connection -> cleanup
 */

export const DEFAULT_NATS_SERVER = "wss://nats.classdojo.com";
export const CONNECTION_TIMEOUT_MS = 5000;
export const PING_INTERVAL_MS = 10000;
export const MAX_PING_OUT = 2;
export const INITIAL_RECONNECT_DELAY_MS = 1000;
export const MAX_RECONNECT_DELAY_MS = 30000;
// NOTF-5: raised 10 → 20 to ride out real school-network blips instead of
// force-closing on them. With the full-jitter backoff below (cap grows
// 2s→4→8→16→30s, then flat 30s), 10 attempts exhausted in ~90s expected — short
// enough that a class changing rooms or an AP handoff tripped the "user has no
// realtime" banner. 20 attempts pushes the expected window to ~3-4 min, covering
// a realistic interruption without hammering (per-attempt delays are unchanged)
// or waiting forever. The banner then fires closer to a genuine sustained outage.
export const MAX_RECONNECT_ATTEMPTS = 20;
export const MAX_INIT_ATTEMPTS = 3;
export const INIT_RETRY_DELAY_MS = 1000;
export const INIT_COOLDOWN_MIN_MS = 30_000;
export const INIT_COOLDOWN_JITTER_MS = 30_000;

export interface NatsAdapterOptions {
  server: string;
  name?: string;
  onConnectionExhausted?: () => void;
  onSubscriptionError?: (error: Error) => void;
  onInitError?: () => void;
  // Fired when the NATS library completes a transient reconnect (status.type === "reconnect").
  // Subscriptions survive automatically, but any events published during the gap are lost —
  // so listeners should refetch any state that might have diverged.
  // `disconnectMs` is the elapsed time since the first preceding disconnect/staleConnection
  // event, so listeners can skip refetches for transient sub-second flaps where no events
  // were realistically lost. 0 if no prior disconnect was observed.
  onReconnect?: (disconnectMs: number) => void;
  // Fired once when the adapter completes its initial connection (not on reconnects).
  // Used by the hook layer to refund the recovery budget when a fresh adapter build
  // (triggered by performRecovery) successfully connects, closing the symmetry gap
  // that let rebuild loops walk the budget down to exhaustion even when every rebuild
  // connected fine (NOTF-3 Layer 2).
  onConnect?: () => void;
  metrics?: RealtimeMetrics;
}

// NOTE: @nats-io/nats-core has no stable error codes, so these classifiers gate
// on the exact name/message strings the library emits. A library reword would
// silently revert this file to noisy behavior — the tests pin the literal
// strings against the installed version to catch that on upgrade.

// Terminal identity/permission rejection — actionable, kept at Sentry error level.
function isAuthorizationViolation(error: unknown): boolean {
  return error instanceof Error && error.name === "AuthorizationError" && error.message === "Authorization Violation";
}

// Auth-callout responder timing out — common during pod rotations when many
// clients reconnect at once. Transient (the client reconnects on its own), so
// observed via metrics rather than reported to Sentry as handled-but-noisy.
function isAuthTimeout(error: unknown): boolean {
  return error instanceof Error && error.name === "AuthorizationError" && error.message === "Authentication Timeout";
}

// Stale Connection — the server closed the link because the client missed
// pings (tab backgrounded, network blip, pod rotation). Transient and
// self-healing via the built-in reconnect machinery, so observed via metrics
// rather than reported to Sentry.
function isStaleConnection(error: unknown): boolean {
  return error instanceof Error && error.name === "ProtocolError" && error.message === "Stale Connection";
}

// Closed set of metric tag values for an init failure — raw error.name is
// open-ended and would explode tag cardinality under the mass-reconnect storms
// this metric exists to observe.
function initFailureReason(error: unknown): string {
  if (isAuthorizationViolation(error)) return "auth_violation";
  if (isAuthTimeout(error)) return "auth_timeout";
  if (error instanceof Error) {
    if (error.name === "TimeoutError") return "timeout";
    if (error.name === "ConnectionError") return "connection";
  }
  return "other";
}

export class NatsAdapter implements RealtimeAdapter {
  #server: string;
  #name: string | undefined;
  #connection: NatsConnection | undefined;
  #subscriptions = new Map<string, Subscription>();
  #initError: Error | null = null;
  #onConnectionExhausted?: () => void;
  #onSubscriptionError?: (error: Error) => void;
  #onInitError?: () => void;
  #onReconnect?: (disconnectMs: number) => void;
  #onConnect?: () => void;
  #metrics?: RealtimeMetrics;
  // Timestamp of the first disconnect/staleConnection event in the current
  // disconnected window. Reset to undefined on reconnect. Tracking the FIRST
  // event (not the latest) gives the longest, most conservative duration —
  // important so a flapping connection is treated as a genuine outage.
  #lastDisconnectAt: number | undefined;
  // Consecutive reconnect attempts since the last successful connection; drives
  // both the backoff cap (2^n) and the hard-close at MAX_RECONNECT_ATTEMPTS.
  // Counts consecutive — not lifetime — failures (reset in #handleReconnectStatus).
  #reconnectAttempts = 0;
  #hasEverConnected = false;
  #statusMonitorAbort: AbortController | undefined;
  #initPromise: Promise<void> | null = null;
  #lastInitFailureTime = 0;
  #currentInitCooldownMs = INIT_COOLDOWN_MIN_MS;

  constructor(options: NatsAdapterOptions) {
    this.#server = options.server;
    this.#name = options.name;
    this.#onConnectionExhausted = options.onConnectionExhausted;
    this.#onSubscriptionError = options.onSubscriptionError;
    this.#onInitError = options.onInitError;
    this.#onReconnect = options.onReconnect;
    this.#onConnect = options.onConnect;
    this.#metrics = options.metrics;
  }

  async init(): Promise<void> {
    if (this.#connection) {
      return;
    }

    if (this.#initPromise) {
      return this.#initPromise;
    }

    if (this.#initError && Date.now() - this.#lastInitFailureTime < this.#currentInitCooldownMs) {
      return;
    }

    this.#initPromise = this.#doInit();
    try {
      await this.#initPromise;
    } finally {
      this.#initPromise = null;
    }
  }

  async #doInit(): Promise<void> {
    this.#initError = null;

    for (let attempt = 1; attempt <= MAX_INIT_ATTEMPTS; attempt++) {
      try {
        const { wsconnect } = await import("@nats-io/nats-core");
        this.#connection = await wsconnect({
          servers: [this.#server],
          name: this.#name,
          reconnect: true,
          // Backstop only: we force-close from reconnectDelayHandler below the
          // moment #reconnectAttempts hits the cap, so that path is authoritative.
          // Both use the same consecutive-failure semantics (the library resets
          // its own counter on a successful connect, as we reset ours).
          maxReconnectAttempts: MAX_RECONNECT_ATTEMPTS,
          reconnectTimeWait: INITIAL_RECONNECT_DELAY_MS,
          reconnectDelayHandler: () => {
            this.#reconnectAttempts++;
            if (this.#reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
              this.#metrics?.increment("nats.reconnect_exhausted");
              // C3: the never-recovered tail of the connection-downtime distribution.
              // Pairs with the outcome:"recovered" emit in #handleReconnectStatus —
              // together they show how long the client's NATS connection stays down
              // whether or not it heals, so a raised reconnect budget can be judged
              // against the full picture.
              if (this.#lastDisconnectAt !== undefined) {
                this.#metrics?.distribution?.(
                  "nats.connection_downtime_ms",
                  performance.now() - this.#lastDisconnectAt,
                  {
                    outcome: "exhausted",
                  },
                );
              }
              this.#onConnectionExhausted?.();
              void this.close();
              return MAX_RECONNECT_DELAY_MS;
            }
            const cap = Math.min(
              Math.pow(2, this.#reconnectAttempts) * INITIAL_RECONNECT_DELAY_MS,
              MAX_RECONNECT_DELAY_MS,
            );
            return Math.random() * cap;
          },
          timeout: CONNECTION_TIMEOUT_MS,
          pingInterval: PING_INTERVAL_MS,
          maxPingOut: MAX_PING_OUT,
        });
        this.#initError = null;
        this.#hasEverConnected = true;
        this.#metrics?.increment("nats.connection_established", { type: "initial" });
        // Fire onConnect AFTER the connection is confirmed established but BEFORE
        // starting the monitors, so the hook can refund the recovery budget
        // without a race from status events on this same connection.
        this.#onConnect?.();
        this.#monitorConnection();
        this.#monitorStatus();
        return;
        // eslint-disable-next-line no-catch-all/no-catch-all -- pre-existing violation
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        this.#initError = error instanceof Error ? error : new Error(errorMessage);
        const isTerminal = isAuthorizationViolation(error);

        if (!isTerminal && attempt < MAX_INIT_ATTEMPTS) {
          const cap = INIT_RETRY_DELAY_MS * Math.pow(2, attempt - 1);
          const delay = Math.random() * cap;
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        this.#lastInitFailureTime = Date.now();
        this.#currentInitCooldownMs = INIT_COOLDOWN_MIN_MS + Math.random() * INIT_COOLDOWN_JITTER_MS;

        // Init failures are overwhelmingly transient and self-healing — the
        // hook-level recovery loop rebuilds the connection and only declares a
        // genuine "NATS recovery exhausted" error once the user truly loses
        // realtime. So observe the rate via a metric, capture at error level only
        // for terminal auth violations (others go to warning, off the error
        // stream), and leave a breadcrumb so the failure detail also trails into
        // any later recovery-exhausted capture in the same Sentry session.
        this.#metrics?.increment("nats.init_failed", { reason: initFailureReason(error) });
        addBreadcrumb({
          category: "nats",
          message: "NATS init failed",
          level: "warning",
          data: { server: this.#server, errorMessage, attempts: attempt },
        });
        captureException(this.#initError, {
          level: isTerminal ? "error" : "warning",
          extra: {
            server: this.#server,
            errorMessage,
            attempts: attempt,
          },
          tags: {
            component: "NatsAdapter",
            operation: "init",
            // In tags (not extra) so Sentry can split init failures by network state —
            // see createRealtimeHook recoveryExhausted capture.
            ...getClientNetworkState(),
          },
        });
        this.#onInitError?.();
        return;
      }
    }
  }

  subscribe(topic: string): RealtimeSubscription {
    if (this.#initError || !this.#connection) {
      const reason = this.#initError?.message ?? this.#initError?.name ?? "unknown error";
      const error = new NatsError(`Cannot subscribe: NATS failed to initialize: ${reason}`);
      error.isInitFailure = !!this.#initError;
      throw error;
    }

    const sub = this.#subscriptions.get(topic);

    if (sub) {
      return sub;
    }

    try {
      const sub = this.#connection.subscribe(topic);
      this.#subscriptions.set(topic, sub);
      return sub;
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : String(error);
      throw new NatsError(`Failed to subscribe: ${errMsg}`);
    }
  }

  unsubscribe(topic: string): void {
    const sub = this.#subscriptions.get(topic);

    if (sub) {
      sub.unsubscribe();
      this.#subscriptions.delete(topic);
    }
  }

  publish(topic: string, data: Uint8Array): void {
    // No-op if not connected (e.g. feature switch is off and no connection established)
    this.#connection?.publish(topic, data);
  }

  async close(): Promise<void> {
    this.#onConnectionExhausted = undefined;
    this.#statusMonitorAbort?.abort();
    this.#statusMonitorAbort = undefined;
    for (const sub of this.#subscriptions.values()) {
      sub.unsubscribe();
    }
    this.#subscriptions.clear();

    if (this.#connection) {
      try {
        await this.#connection.close();
        // eslint-disable-next-line no-catch-all/no-catch-all -- pre-existing violation
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        captureException(error instanceof Error ? error : new Error(errorMessage), {
          extra: {
            operation: "close",
            errorMessage,
          },
          tags: {
            component: "NatsAdapter",
            operation: "close",
          },
        });
      } finally {
        this.#connection = undefined;
      }
    }
  }

  #monitorConnection(): void {
    if (!this.#connection) return;

    void this.#connection
      .closed()
      .then(() => {
        this.#onConnectionExhausted?.();
      })
      .catch((error) => {
        captureException(error instanceof Error ? error : new Error(String(error)), {
          tags: { component: "NatsAdapter", operation: "monitorConnection" },
        });
      });
  }

  #handleReconnectStatus(): void {
    // A successful reconnect ends the failure episode: reset the counter so both
    // the backoff and the exhaustion budget start fresh. Without this, a
    // long-lived tab on a flaky-but-recovering network accumulates attempts
    // across healthy episodes and eventually force-closes a working connection
    // (surfacing the persistent "difficulty syncing" banner — NOTF-3).
    this.#reconnectAttempts = 0;
    this.#metrics?.increment("nats.connection_established", { type: "reconnect" });
    // performance.now() is monotonic — Date.now() can jump backwards on NTP
    // corrections (notably after suspend/resume on cloud VMs), which would yield
    // a negative disconnectMs that silently passes the consumer's <1s gate.
    const disconnectedAt = this.#lastDisconnectAt;
    const hadDrop = disconnectedAt !== undefined;
    const disconnectMs = hadDrop ? performance.now() - disconnectedAt : 0;
    this.#lastDisconnectAt = undefined;
    // C3: how long the client's NATS connection was down before we recovered. As a
    // distribution so Datadog gives p50/p95 blip length — that's what tells us
    // whether a given reconnect budget (NOTF-5) covers real interruptions. Emitted
    // whenever a drop preceded this reconnect (including sub-second flaps, which are
    // legitimately the short tail); outcome:"recovered" separates it from the
    // never-recovered force-close below.
    if (hadDrop) {
      this.#metrics?.distribution?.("nats.connection_downtime_ms", disconnectMs, { outcome: "recovered" });
    }
    this.#onReconnect?.(disconnectMs);
  }

  #handleDropStatus(reason: "disconnect" | "stale"): void {
    this.#metrics?.increment("nats.connection_dropped", { reason });
    // Track only the FIRST event in a disconnect window so the duration measured
    // on reconnect represents the longest plausible outage. A flapping connection
    // (multiple drops without intervening reconnect) keeps the original timestamp.
    if (this.#lastDisconnectAt === undefined) this.#lastDisconnectAt = performance.now();
  }

  // Returns true if the caller should break out of the status loop.
  #handleErrorStatus(error: Error): boolean {
    // Authentication Timeout is transient — the NATS client reconnects on its
    // own — so we record a metric for observability but do NOT captureException,
    // which previously flooded Sentry with handled, self-healing
    // "AuthorizationError: Authentication Timeout" error events. If the timeouts
    // persist long enough to drop the connection, the reconnect/recovery path
    // still surfaces the genuine "recovery exhausted" signal.
    if (isAuthTimeout(error)) {
      this.#metrics?.increment("nats.auth_timeout");
      addBreadcrumb({
        category: "nats",
        message: "NATS auth timeout (transient, awaiting reconnect)",
        level: "warning",
        data: { server: this.#server },
      });
      return false;
    }

    if (isStaleConnection(error)) {
      this.#metrics?.increment("nats.stale_connection");
      addBreadcrumb({
        category: "nats",
        message: "NATS stale connection (transient, awaiting reconnect)",
        level: "warning",
        data: { server: this.#server },
      });
      return false;
    }

    captureException(error, {
      extra: { server: this.#server },
      tags: { component: "NatsAdapter", operation: "statusError" },
      fingerprint: getPermissionViolationFingerprint(error),
    });
    if (isAuthorizationViolation(error)) {
      void this.close();
      return true;
    }
    if (this.#hasEverConnected) this.#onSubscriptionError?.(error);
    return false;
  }

  #monitorStatus(): void {
    if (!this.#connection) return;

    this.#statusMonitorAbort = new AbortController();
    const signal = this.#statusMonitorAbort.signal;

    (async () => {
      for await (const status of this.#connection!.status()) {
        if (signal.aborted) break;
        if (status.type === "reconnect") this.#handleReconnectStatus();
        if (status.type === "disconnect") this.#handleDropStatus("disconnect");
        if (status.type === "staleConnection") this.#handleDropStatus("stale");
        if (status.type === "error" && this.#handleErrorStatus(status.error)) break;
      }
    })().catch((error) => {
      if (!signal.aborted) {
        captureException(error instanceof Error ? error : new Error(String(error)), {
          tags: { component: "NatsAdapter", operation: "monitorStatus" },
        });
      }
    });
  }
}

export class NatsError extends Error {
  isNatsError = true;
  isInitFailure = false;
  constructor(message: string) {
    super(message);
  }
}
